import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import $ from 'jquery';
import config from '../../../../../config';

/** Pause/resume controls in the priority column. Default off; in DevTools: `window.__MAM_RENDER_QUEUE_PAUSE_RESUME_UI__ = true` then trigger a re-render. */
function isRenderQueuePauseResumeUiEnabled() {
  return typeof window !== 'undefined' && window.__MAM_RENDER_QUEUE_PAUSE_RESUME_UI__ === true;
}

class RenderQueuePane extends Component {
  constructor(props) {
    super(props);
    
    // Define all available columns
    this.columnDefinitions = {
      id: { key: 'id', label: 'ID', defaultVisible: false },
      actionID: { key: 'actionID', label: 'Action ID', defaultVisible: true },
      woMemfis: { key: 'woMemfis', label: 'WO Memfis', defaultVisible: true },
      project: { key: 'project', label: 'Project', defaultVisible: true },
      status: { key: 'status', label: 'Status', defaultVisible: true },
      hotFolderType: { key: 'hotFolderType', label: 'Hot Folder Type', defaultVisible: true },
      startTime: { key: 'startTime', label: 'Start Time', defaultVisible: true },
      timeLeft: { key: 'timeLeft', label: 'Time Left', defaultVisible: true },
      priority: { key: 'priority', label: 'Priority', defaultVisible: true },
      projectTitle: { key: 'projectTitle', label: 'Project Title', defaultVisible: false },
      projectName: { key: 'projectName', label: 'Project Name', defaultVisible: false },
      projectValue: { key: 'projectValue', label: 'Project Value', defaultVisible: false },
      projectDate: { key: 'projectDate', label: 'Project Date', defaultVisible: false },
      eventType: { key: 'eventType', label: 'Event Type', defaultVisible: false },
      hostSubmit: { key: 'hostSubmit', label: 'Host Submit', defaultVisible: false },
      hostRender: { key: 'hostRender', label: 'Host Render', defaultVisible: false },
      submitTime: { key: 'submitTime', label: 'Submit Time', defaultVisible: false },
      errors: { key: 'errors', label: 'Errors', defaultVisible: false },
      hostTarget: { key: 'hostTarget', label: 'Host Target', defaultVisible: false },
      jobCommand: { key: 'jobCommand', label: 'Job Command', defaultVisible: false },
      jobType: { key: 'jobType', label: 'Job Type', defaultVisible: false },
      projectContainer: { key: 'projectContainer', label: 'Project Container', defaultVisible: false },
      proxy: { key: 'proxy', label: 'Proxy', defaultVisible: false },
      renderEndTime: { key: 'renderEndTime', label: 'Render End Time', defaultVisible: false },
      speed: { key: 'speed', label: 'Speed', defaultVisible: false },
      timeFinish: { key: 'timeFinish', label: 'Time Finish', defaultVisible: false },
      uuid: { key: 'uuid', label: 'UUID', defaultVisible: false },
      renderCommand: { key: 'renderCommand', label: 'Render Command', defaultVisible: false },
      applicationVersion: { key: 'applicationVersion', label: 'Application Version', defaultVisible: false },
      fileName: { key: 'fileName', label: 'File Name', defaultVisible: false },
      progress: { key: 'progress', label: 'Progress', defaultVisible: false },
      duration: { key: 'duration', label: 'Duration', defaultVisible: false }
    };
    
    this.state = {
      queueIDs: [],
      selectedQueueID: null,
      jobs: [],
      loading: false,
      selectedJobID: null,
      jobDetails: null,
      actionAssociations: {}, // jobID -> actionID
      rightPaneWidth: 400, // Default width for job details pane
      isResizing: false,
      visibleColumns: this.getInitialColumnVisibility(),
      showColumnOverlay: false,
      editingPriority: null, // {jobID: number, value: string}
      priorityMinValue: -2147483648,
      priorityMaxValue: 2147483647,
      pausedPriorityValue: -2147483649, // Value outside valid range to represent paused (internal only)
      resumedPriorityValue: 2147483648 // Value outside valid range to represent resume (internal only)
    };
  }
  
  getInitialColumnVisibility = () => {
    const defaultColumns = {};
    Object.keys(this.columnDefinitions).forEach(key => {
      defaultColumns[key] = this.columnDefinitions[key].defaultVisible;
    });
    
    try {
      const saved = localStorage.getItem('renderQueuePane_columnVisibility');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults to handle new columns
        return { ...defaultColumns, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load column visibility settings:', error);
    }
    
    return defaultColumns;
  };
  
  saveColumnVisibility = (visibleColumns) => {
    try {
      localStorage.setItem('renderQueuePane_columnVisibility', JSON.stringify(visibleColumns));
    } catch (error) {
      console.warn('Failed to save column visibility settings:', error);
    }
  };
  
  handleColumnToggle = (columnKey) => {
    const { visibleColumns } = this.state;
    const visibleCount = Object.values(visibleColumns).filter(v => v).length;
    const isLastVisible = visibleCount === 1 && visibleColumns[columnKey];
    
    if (isLastVisible) {
      // Prevent hiding the last visible column
      return;
    }
    
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    
    this.setState({ visibleColumns: newVisibleColumns });
    this.saveColumnVisibility(newVisibleColumns);
  };
  
  handleResetColumns = () => {
    const defaultColumns = {};
    Object.keys(this.columnDefinitions).forEach(key => {
      defaultColumns[key] = this.columnDefinitions[key].defaultVisible;
    });
    this.setState({ visibleColumns: defaultColumns });
    this.saveColumnVisibility(defaultColumns);
  };
  
  toggleColumnOverlay = () => {
    this.setState(prevState => ({
      showColumnOverlay: !prevState.showColumnOverlay
    }));
  };
  
  closeColumnOverlay = () => {
    this.setState({ showColumnOverlay: false });
  };

  componentDidMount() {
    this.loadQueueIDs();
    // If initialQueueID is provided, select it
    if (this.props.initialQueueID) {
      this.setState({ selectedQueueID: this.props.initialQueueID }, () => {
        this.loadJobs(this.props.initialQueueID);
      });
    }
    // If initialJobID is provided, select it after jobs are loaded
    if (this.props.initialJobID && this.props.initialQueueID) {
      // Will be handled in componentDidUpdate after jobs load
    }
    
    // Add escape key handler for column overlay
    document.addEventListener('keydown', this.handleKeyDown);
  }
  
  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }
  
  handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.state.showColumnOverlay) {
      this.closeColumnOverlay();
    }
  };

  componentDidUpdate(prevProps) {
    // If initialQueueID or initialJobID changed, update selection
    if (this.props.initialQueueID !== prevProps.initialQueueID) {
      if (this.props.initialQueueID) {
        this.setState({ selectedQueueID: this.props.initialQueueID }, () => {
          this.loadJobs(this.props.initialQueueID);
        });
      }
    }
    if (this.props.initialJobID !== prevProps.initialJobID && this.props.initialJobID) {
      // Select the job after jobs are loaded
      if (this.state.jobs.length > 0) {
        this.selectJob(this.props.initialJobID);
      }
    }
  }

  loadQueueIDs = () => {
    $.ajax({
      method: 'GET',
      url: `${config.apiUrl}/v1/file_system/mam/transkoder/render_queue/list`,
      dataType: 'json'
    })
    .done((data) => {
      if (data && data.queueIDs) {
        this.setState({ queueIDs: data.queueIDs });
        // Auto-select first queue if none selected
        if (!this.state.selectedQueueID && data.queueIDs.length > 0) {
          this.setState({ selectedQueueID: data.queueIDs[0] }, () => {
            this.loadJobs(data.queueIDs[0]);
          });
        }
      }
    })
    .fail((xhr, status, error) => {
      console.error('Failed to load queue IDs:', error);
    });
  };

  loadJobs = (queueID) => {
    if (!queueID) return;
    
    this.setState({ loading: true, jobs: [], selectedJobID: null, jobDetails: null });
    
    $.ajax({
      method: 'GET',
      url: `${config.apiUrl}/v1/file_system/mam/transkoder/render_queue/jobs`,
      data: { queueID },
      dataType: 'json'
    })
    .done((data) => {
      if (data && data.jobs) {
        const actionAssociations = {};
        data.jobs.forEach(job => {
          if (job.actionID) {
            actionAssociations[job.id] = job.actionID;
          }
        });
        
        this.setState({ 
          jobs: data.jobs, 
          loading: false,
          actionAssociations
        });
        
        // If initialJobID is provided, select it
        if (this.props.initialJobID) {
          this.selectJob(this.props.initialJobID);
        }
      } else {
        this.setState({ loading: false });
      }
    })
    .fail((xhr, status, error) => {
      console.error('Failed to load jobs:', error);
      this.setState({ loading: false });
    });
  };

  handleQueueChange = (e) => {
    const queueID = parseInt(e.target.value, 10);
    this.setState({ selectedQueueID: queueID });
    this.loadJobs(queueID);
  };

  selectJob = (jobID) => {
    const job = this.state.jobs.find(j => j.id === jobID);
    if (job) {
      this.setState({ 
        selectedJobID: jobID,
        jobDetails: job.jobDetails ? JSON.parse(job.jobDetails) : null
      }, () => {
        // Scroll the selected row into view
        setTimeout(() => {
          const rowElement = document.querySelector(`[data-job-id="${jobID}"]`);
          if (rowElement) {
            rowElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      });
    }
  };

  handleJobClick = (jobID) => {
    this.selectJob(jobID);
  };

  handleActionClick = (actionID) => {
    console.log('[RenderQueuePane] handleActionClick called with actionID:', actionID);
    console.log('[RenderQueuePane] this.props.onNavigateToAction:', this.props.onNavigateToAction);
    if (this.props.onNavigateToAction) {
      console.log('[RenderQueuePane] Calling onNavigateToAction with actionID:', actionID);
      this.props.onNavigateToAction(actionID);
    } else {
      console.warn('[RenderQueuePane] onNavigateToAction prop is not defined!');
    }
  };

  handleCloseJobDetails = () => {
    this.setState({ selectedJobID: null, jobDetails: null });
  };

  handleMouseDown = (e) => {
    this.setState({ isResizing: true });
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  };

  handleMouseMove = (e) => {
    if (!this.state.isResizing) return;
    
    const containerWidth = (this.containerRef && this.containerRef.clientWidth) || window.innerWidth;
    const newWidth = containerWidth - e.clientX;
    
    // Min width 200px, max width 80% of container
    const minWidth = 200;
    const maxWidth = containerWidth * 0.8;
    
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      this.setState({ rightPaneWidth: newWidth });
    }
  };

  handleMouseUp = () => {
    this.setState({ isResizing: false });
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  };

  formatStatus = (job) => {
    // Use status from job (from tk_render_queue_latest) as primary, fallback to statusFromDetails
    const status = job.status || job.statusFromDetails || 'Unknown';
    // Use progress from job (from tk_render_queue_latest) as primary, fallback to progressFromDetails
    // Handle both string and number progress values
    let progress = job.progress !== undefined ? job.progress : job.progressFromDetails;
    if (typeof progress === 'string') {
      progress = parseInt(progress, 10);
    }
    
    if (progress !== undefined && !isNaN(progress) && progress >= 0 && progress <= 100) {
      return `${status} (${progress}%)`;
    }
    return status;
  };
  
  getStatusColor = (job) => {
    const status = (job.status || job.statusFromDetails || '').toLowerCase();
    
    if (status === 'failed' || status === 'error' || status === 'failure') {
      return '#dc3545'; // red
    } else if (status === 'success' || status === 'completed' || status === 'complete') {
      return '#28a745'; // green
    } else if (status === 'pending' || status === 'queued' || status === 'waiting') {
      return '#ffc107'; // yellow
    } else if (status === 'active' || status === 'running' || status === 'processing') {
      return '#007bff'; // blue
    }
    
    return '#6c757d'; // default gray
  };
  
  isJobComplete = (job) => {
    const status = (job.status || job.statusFromDetails || '').toLowerCase();
    return status === 'success';
  };
  
  isJobPaused = (job) => {
    const status = (job.status || job.statusFromDetails || '').toLowerCase();
    return status === 'paused';
  };
  
  isJobFailed = (job) => {
    const status = (job.status || job.statusFromDetails || '').toLowerCase();
    return status === 'failed' || status === 'error' || status === 'failure';
  };
  
  isJobPausable = (job) => {
    // Only show pause button for these active statuses
    const status = (job.status || job.statusFromDetails || '').toLowerCase();
    return ['pending', 'running', 'rendering', 'processing'].includes(status);
  };
  
  isJobTerminal = (job) => {
    const status = (job.status || job.statusFromDetails || '').toLowerCase();
    return ['success', 'failure', 'failed', 'error', 'complete', 'completed'].includes(status);
  };
  
  handlePauseJob = (e, job) => {
    e.stopPropagation();
    if (!this.isJobPausable(job)) {
      return;
    }
    if (window.confirm('Are you sure you want to pause this job?')) {
      this.updateTargetPriority(job.id, this.state.pausedPriorityValue);
    }
  };
  
  handleResumeJob = (e, job) => {
    e.stopPropagation();
    if (!this.isJobPaused(job)) {
      return;
    }
    if (window.confirm('Are you sure you want to resume this job?')) {
      this.updateTargetPriority(job.id, this.state.resumedPriorityValue);
    }
  };
  
  handlePriorityClick = (e, job) => {
    e.stopPropagation();
    if (this.isJobTerminal(job)) {
      return; // Don't allow editing for jobs in terminal statuses
    }
    this.setState({
      editingPriority: {
        jobID: job.id,
        value: job.priority || ''
      }
    });
  };
  
  handlePriorityChange = (e, job) => {
    const value = e.target.value;
    this.setState({
      editingPriority: {
        jobID: job.id,
        value: value
      }
    });
  };
  
  handlePriorityBlur = (e, job) => {
    const { editingPriority } = this.state;
    if (!editingPriority || editingPriority.jobID !== job.id) {
      return;
    }
    
    const newValue = editingPriority.value.trim();
    const currentPriority = job.priority || '';
    
    // If value didn't change, just cancel editing
    if (newValue === currentPriority) {
      this.setState({ editingPriority: null });
      return;
    }
    
    // Validate value
    const numValue = parseInt(newValue, 10);
    if (isNaN(numValue)) {
      // Invalid number, revert
      this.setState({ editingPriority: null });
      return;
    }
    
    // Only allow values within valid range (no out-of-range values allowed in UI)
    if (numValue < this.state.priorityMinValue || numValue > this.state.priorityMaxValue) {
      alert(`Priority must be between ${this.state.priorityMinValue} and ${this.state.priorityMaxValue}`);
      this.setState({ editingPriority: null });
      return;
    }
    
    // Update TargetPriority via API
    this.updateTargetPriority(job.id, numValue);
  };
  
  handlePriorityKeyDown = (e, job) => {
    if (e.key === 'Enter') {
      this.handlePriorityBlur(e, job);
    } else if (e.key === 'Escape') {
      this.setState({ editingPriority: null });
    }
  };
  
  updateTargetPriority = (jobID, targetPriority) => {
    const { selectedQueueID } = this.state;
    
    $.ajax({
      method: 'POST',
      url: `${config.apiUrl}/v1/file_system/mam/transkoder/render_queue/update_target_priority`,
      data: JSON.stringify({
        queueID: selectedQueueID,
        jobID: jobID,
        targetPriority: targetPriority
      }),
      contentType: 'application/json',
      dataType: 'json'
    })
    .done((response) => {
      if (response.success) {
        // Reload jobs to get updated data
        this.loadJobs(selectedQueueID);
        this.setState({ editingPriority: null });
      } else {
        alert('Failed to update priority: ' + (response.message || 'Unknown error'));
        this.setState({ editingPriority: null });
      }
    })
    .fail((xhr, status, error) => {
      console.error('Failed to update target priority:', error);
      alert('Failed to update priority: ' + error);
      this.setState({ editingPriority: null });
    });
  };

  calculateProject = (job) => {
    // Requirement 7: Calculate Project column from ProjectName and ProjectTitle
    const projectName = job.projectName || '';
    const projectTitle = job.projectTitle || null;
    
    if (projectTitle === null) {
      return projectName || '-';
    }
    
    if (!projectName || projectName.length <= 1) {
      return projectTitle;
    }
    
    return `${projectName} (${projectTitle})`;
  };

  render() {
    const { queueIDs, selectedQueueID, jobs, loading, selectedJobID, jobDetails, actionAssociations, rightPaneWidth, visibleColumns, showColumnOverlay } = this.state;
    const showJobDetails = selectedJobID !== null && jobDetails !== null;
    
    // Get visible column keys in order
    const visibleColumnKeys = Object.keys(this.columnDefinitions).filter(key => visibleColumns[key]);
    const visibleCount = visibleColumnKeys.length;

    return (
      <Container ref={el => this.containerRef = el}>
        <Header>
          <QueueSelector>
            <label>Queue ID:</label>
            <select value={selectedQueueID || ''} onChange={this.handleQueueChange}>
              <option value="">Select Queue...</option>
              {queueIDs.map(qid => (
                <option key={qid} value={qid}>{qid}</option>
              ))}
            </select>
          </QueueSelector>
          <HeaderRight>
            <ColumnSettingsButton onClick={this.toggleColumnOverlay} title="Column Settings">
              <i className="fa fa-cog" /> Columns
            </ColumnSettingsButton>
            {this.props.onBackToActions && (
              <BackButton onClick={this.props.onBackToActions}>
                ← Back to Actions
              </BackButton>
            )}
          </HeaderRight>
        </Header>
        
        {showColumnOverlay && (
          <ColumnVisibilityOverlay onClick={this.closeColumnOverlay}>
            <ColumnVisibilityContent onClick={(e) => e.stopPropagation()}>
              <ColumnVisibilityHeader>
                <span>Column Visibility</span>
                <CloseButton onClick={this.closeColumnOverlay}>×</CloseButton>
              </ColumnVisibilityHeader>
              <ColumnVisibilityList>
                {Object.keys(this.columnDefinitions).map(columnKey => {
                  const column = this.columnDefinitions[columnKey];
                  const isVisible = visibleColumns[columnKey];
                  const isLastVisible = visibleCount === 1 && isVisible;
                  
                  return (
                    <ColumnVisibilityItem key={columnKey}>
                      <label>
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => this.handleColumnToggle(columnKey)}
                          disabled={isLastVisible}
                          title={isLastVisible ? "Cannot hide the last visible column" : ""}
                        />
                        {column.label}
                      </label>
                    </ColumnVisibilityItem>
                  );
                })}
              </ColumnVisibilityList>
              <ColumnVisibilityFooter>
                <ResetButton onClick={this.handleResetColumns}>Reset to Default</ResetButton>
              </ColumnVisibilityFooter>
            </ColumnVisibilityContent>
          </ColumnVisibilityOverlay>
        )}

        <ContentContainer>
          <TableContainer style={{ width: showJobDetails ? `calc(100% - ${rightPaneWidth}px - 10px)` : '100%' }}>
            {loading ? (
              <LoadingMessage>Loading jobs...</LoadingMessage>
            ) : (
              <Table>
                <thead>
                  <tr>
                    {visibleColumns.id && <th>ID</th>}
                    {visibleColumns.actionID && <th>Action ID</th>}
                    {visibleColumns.woMemfis && <th>WO Memfis</th>}
                    {visibleColumns.project && <th>Project</th>}
                    {visibleColumns.status && <th>Status</th>}
                    {visibleColumns.hotFolderType && <th>Hot Folder Type</th>}
                    {visibleColumns.startTime && <th>Start Time</th>}
                    {visibleColumns.timeLeft && <th>Time Left</th>}
                    {visibleColumns.priority && <th>Priority</th>}
                    {visibleColumns.projectTitle && <th>Project Title</th>}
                    {visibleColumns.projectName && <th>Project Name</th>}
                    {visibleColumns.projectValue && <th>Project Value</th>}
                    {visibleColumns.projectDate && <th>Project Date</th>}
                    {visibleColumns.eventType && <th>Event Type</th>}
                    {visibleColumns.hostSubmit && <th>Host Submit</th>}
                    {visibleColumns.hostRender && <th>Host Render</th>}
                    {visibleColumns.submitTime && <th>Submit Time</th>}
                    {visibleColumns.errors && <th>Errors</th>}
                    {visibleColumns.hostTarget && <th>Host Target</th>}
                    {visibleColumns.jobCommand && <th>Job Command</th>}
                    {visibleColumns.jobType && <th>Job Type</th>}
                    {visibleColumns.projectContainer && <th>Project Container</th>}
                    {visibleColumns.proxy && <th>Proxy</th>}
                    {visibleColumns.renderEndTime && <th>Render End Time</th>}
                    {visibleColumns.speed && <th>Speed</th>}
                    {visibleColumns.timeFinish && <th>Time Finish</th>}
                    {visibleColumns.uuid && <th>UUID</th>}
                    {visibleColumns.renderCommand && <th>Render Command</th>}
                    {visibleColumns.applicationVersion && <th>Application Version</th>}
                    {visibleColumns.fileName && <th>File Name</th>}
                    {visibleColumns.progress && <th>Progress</th>}
                    {visibleColumns.duration && <th>Duration</th>}
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => (
                    <TableRow 
                      key={job.id}
                      data-job-id={job.id}
                      selected={selectedJobID === job.id}
                      onClick={() => this.handleJobClick(job.id)}
                    >
                      {visibleColumns.id && <td>{job.id}</td>}
                      {visibleColumns.actionID && (
                        <td>
                          {actionAssociations[job.id] ? (
                            <ActionLink onClick={(e) => {
                              console.log('[RenderQueuePane] ActionLink clicked for job.id:', job.id, 'actionID:', actionAssociations[job.id]);
                              e.stopPropagation();
                              this.handleActionClick(actionAssociations[job.id]);
                            }}>
                              {actionAssociations[job.id]}
                            </ActionLink>
                          ) : '-'}
                        </td>
                      )}
                      {visibleColumns.woMemfis && <td>{job.woMemfis || '-'}</td>}
                      {visibleColumns.project && <td>{this.calculateProject(job)}</td>}
                      {visibleColumns.status && (
                        <td>
                          <StatusCell statusColor={this.getStatusColor(job)}>
                            {this.formatStatus(job)}
                          </StatusCell>
                        </td>
                      )}
                      {visibleColumns.hotFolderType && <td>{job.hotFolderType || '-'}</td>}
                      {visibleColumns.startTime && <td>{job.renderStartTime || '-'}</td>}
                      {visibleColumns.timeLeft && (
                        <td>
                          {(() => {
                            const status = (job.status || job.statusFromDetails || '').toLowerCase();
                            const formattedStatus = this.formatStatus(job).toLowerCase();
                            // Check if status is "success" or "success (100%)"
                            if (status === 'success' || formattedStatus === 'success' || formattedStatus === 'success (100%)') {
                              return '0';
                            }
                            return job.timeLeft || '-';
                          })()}
                        </td>
                      )}
                      {visibleColumns.priority && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <PriorityCellContainer>
                            {this.state.editingPriority && this.state.editingPriority.jobID === job.id ? (
                              <PriorityInput
                                type="number"
                                value={this.state.editingPriority.value}
                                onChange={(e) => this.handlePriorityChange(e, job)}
                                onBlur={(e) => this.handlePriorityBlur(e, job)}
                                onKeyDown={(e) => this.handlePriorityKeyDown(e, job)}
                                min={this.state.priorityMinValue}
                                max={this.state.priorityMaxValue}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <>
                                <PriorityCell 
                                  editable={!this.isJobTerminal(job)}
                                  onClick={(e) => this.handlePriorityClick(e, job)}
                                  style={{ cursor: this.isJobTerminal(job) ? 'default' : 'pointer' }}
                                  title={this.isJobTerminal(job) ? 'Cannot edit priority for jobs in terminal status' : 'Click to edit priority'}
                                >
                                  {job.priority || '-'}
                                  {job.targetPriority && job.targetPriority !== job.priority && (
                                    <TargetPriorityIndicator title={`Target priority: ${job.targetPriority}`}>
                                      {' '}(→ {job.targetPriority})
                                    </TargetPriorityIndicator>
                                  )}
                                </PriorityCell>
                                {isRenderQueuePauseResumeUiEnabled() && (
                                  <PriorityActionButtons>
                                    {this.isJobPausable(job) && (
                                      <PauseButton 
                                        onClick={(e) => this.handlePauseJob(e, job)}
                                        title="Pause job"
                                      >
                                        ⏸
                                      </PauseButton>
                                    )}
                                    {this.isJobPaused(job) && (
                                      <ResumeButton 
                                        onClick={(e) => this.handleResumeJob(e, job)}
                                        title="Resume job"
                                      >
                                        ▶
                                      </ResumeButton>
                                    )}
                                  </PriorityActionButtons>
                                )}
                              </>
                            )}
                          </PriorityCellContainer>
                        </td>
                      )}
                      {visibleColumns.projectTitle && <td>{job.projectTitle || '-'}</td>}
                      {visibleColumns.projectName && <td>{job.projectName || '-'}</td>}
                      {visibleColumns.projectValue && <td>{job.projectValue || '-'}</td>}
                      {visibleColumns.projectDate && <td>{job.projectDate || '-'}</td>}
                      {visibleColumns.eventType && <td>{job.eventType || '-'}</td>}
                      {visibleColumns.hostSubmit && <td>{job.hostSubmit || '-'}</td>}
                      {visibleColumns.hostRender && <td>{job.hostRender || '-'}</td>}
                      {visibleColumns.submitTime && <td>{job.submitTime ? new Date(job.submitTime).toLocaleString() : '-'}</td>}
                      {visibleColumns.errors && <td>{job.errors || '-'}</td>}
                      {visibleColumns.hostTarget && <td>{job.hostTarget || '-'}</td>}
                      {visibleColumns.jobCommand && <td>{job.jobCommand || '-'}</td>}
                      {visibleColumns.jobType && <td>{job.jobType || '-'}</td>}
                      {visibleColumns.projectContainer && <td>{job.projectContainer || '-'}</td>}
                      {visibleColumns.proxy && <td>{job.proxy || '-'}</td>}
                      {visibleColumns.renderEndTime && <td>{job.renderEndTime ? new Date(job.renderEndTime).toLocaleString() : '-'}</td>}
                      {visibleColumns.speed && <td>{job.speed || '-'}</td>}
                      {visibleColumns.timeFinish && <td>{job.timeFinish || '-'}</td>}
                      {visibleColumns.uuid && <td>{job.uuid || '-'}</td>}
                      {visibleColumns.renderCommand && <td>{job.renderCommand || '-'}</td>}
                      {visibleColumns.applicationVersion && <td>{job.applicationVersion || '-'}</td>}
                      {visibleColumns.fileName && <td>{job.fileName || '-'}</td>}
                      {visibleColumns.progress && <td>{job.progress || job.progressFromDetails || '-'}</td>}
                      {visibleColumns.duration && <td>{job.duration || '-'}</td>}
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>

          {showJobDetails && (
            <>
              <Resizer 
                onMouseDown={this.handleMouseDown}
                style={{ cursor: 'col-resize' }}
              />
              <JobDetailsPane style={{ width: `${rightPaneWidth}px` }}>
                <JobDetailsHeader>
                  <h3>Job Details</h3>
                  <CloseButton onClick={this.handleCloseJobDetails}>×</CloseButton>
                </JobDetailsHeader>
                <JobDetailsContent>
                  {this.renderJobDetails(jobDetails)}
                </JobDetailsContent>
              </JobDetailsPane>
            </>
          )}
        </ContentContainer>
      </Container>
    );
  }

  renderJobDetails = (details) => {
    if (!details || typeof details !== 'object') {
      return <div>No job details available</div>;
    }

    return (
      <DetailsCard>
        {Object.entries(details).map(([key, value]) => (
          <DetailItem key={key}>
            <DetailLabel>{key}:</DetailLabel>
            <DetailValue>
              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
            </DetailValue>
          </DetailItem>
        ))}
      </DetailsCard>
    );
  };
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dee2e6;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ColumnSettingsButton = styled.button`
  padding: 6px 12px;
  background: #f8f9fa;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
  
  i {
    font-size: 14px;
  }
`;

const ColumnVisibilityOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ColumnVisibilityContent = styled.div`
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 300px;
  max-width: 400px;
`;

const ColumnVisibilityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  font-size: 16px;
  color: #212529;
`;

const ColumnVisibilityList = styled.div`
  padding: 15px;
  max-height: 400px;
  overflow-y: auto;
`;

const ColumnVisibilityItem = styled.div`
  padding: 8px 0;
  
  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #212529;
    
    input[type="checkbox"] {
      cursor: pointer;
      
      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }
`;

const ColumnVisibilityFooter = styled.div`
  padding: 15px;
  border-top: 1px solid #dee2e6;
  display: flex;
  justify-content: flex-end;
`;

const ResetButton = styled.button`
  padding: 6px 12px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #5a6268;
  }
`;

const QueueSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  
  label {
    font-weight: 600;
    color: #495057;
  }
  
  select {
    padding: 6px 12px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-size: 14px;
  }
`;

const BackButton = styled.button`
  padding: 6px 12px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #0056b3;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
`;

const LoadingMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #6c757d;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
`;

const TableRow = styled.tr`
  cursor: pointer;
  border-bottom: 1px solid #dee2e6;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  ${props => props.selected && `
    background-color: #e7f3ff !important;
    border-left: 3px solid #007bff;
  `}
  
  td {
    padding: 12px;
    font-size: 13px;
    color: #212529;
  }
  
  th {
    padding: 12px;
    text-align: left;
    font-weight: 600;
    font-size: 13px;
    color: #495057;
    background: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
  }
`;

const ActionLink = styled.span`
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    color: #0056b3;
  }
`;

const Resizer = styled.div`
  width: 10px;
  background: #dee2e6;
  cursor: col-resize;
  flex-shrink: 0;
  
  &:hover {
    background: #adb5bd;
  }
`;

const JobDetailsPane = styled.div`
  background: white;
  border-left: 1px solid #dee2e6;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
`;

const JobDetailsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  
  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #212529;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: #dee2e6;
    color: #212529;
  }
`;

const JobDetailsContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 15px;
`;

const DetailsCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
`;

const DetailItem = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
  font-size: 13px;
`;

const DetailValue = styled.div`
  color: #212529;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
  background: white;
  padding: 8px;
  border-radius: 3px;
  border: 1px solid #dee2e6;
  font-family: 'Courier New', monospace;
  max-height: 200px;
  overflow-y: auto;
`;

const PriorityCell = styled.span`
  ${props => props.editable && `
    &:hover {
      background-color: #f0f0f0;
      border-radius: 3px;
      padding: 2px 4px;
    }
  `}
`;

const PriorityInput = styled.input`
  width: 100px;
  padding: 4px 6px;
  border: 2px solid #007bff;
  border-radius: 3px;
  font-size: 13px;
  
  &:focus {
    outline: none;
    border-color: #0056b3;
  }
`;

const TargetPriorityIndicator = styled.span`
  color: #ffc107;
  font-weight: 600;
  font-size: 11px;
`;

const PriorityCellContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PriorityActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PauseButton = styled.button`
  background: #ffc107;
  color: white;
  border: none;
  border-radius: 3px;
  padding: 2px 6px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  
  &:hover {
    background: #ffb300;
  }
  
  &:active {
    background: #ffa000;
  }
`;

const ResumeButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  border-radius: 3px;
  padding: 2px 6px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  
  &:hover {
    background: #218838;
  }
  
  &:active {
    background: #1e7e34;
  }
`;

const StatusCell = styled.span`
  color: ${props => props.statusColor || '#6c757d'};
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
`;

RenderQueuePane.propTypes = {
  initialQueueID: PropTypes.number,
  initialJobID: PropTypes.number,
  onNavigateToAction: PropTypes.func,
  onBackToActions: PropTypes.func
};

export default RenderQueuePane;

